import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import AppLinkRedirect from "../components/AppLinkRedirect";
import {
  buildDeepLink,
  decodeAndValidatePublicId,
  validatePublicId
} from "../utils/appLinks";
import { resolveProfileHandle } from "../utils/profileResolver";

const PROFILE_RESOLUTION_TIMEOUT_MS = 5000;

function LoadingProfile() {
  return (
    <section className="min-h-[60vh] bg-[#FAF8F2] px-4 py-20 text-center">
      <div className="mx-auto max-w-md">
        <img
          src="/icons/new_login_logo.svg"
          alt="Mondoway Logo"
          className="mx-auto mb-8 h-16 w-auto"
        />
        <div
          aria-label="Finding profile"
          className="mx-auto mb-5 h-8 w-8 animate-spin rounded-full border-4 border-[#D8B4FE] border-t-[#6A0DAD]"
        />
        <h1 className="mb-3 text-2xl font-bold text-[#0F172A]">
          Finding this profile...
        </h1>
        <p className="text-[#64748B]">
          Please wait while we prepare the correct Mondoway profile.
        </p>
      </div>
    </section>
  );
}

function ProfileRedirect() {
  const location = useLocation();
  const { profileId: routeProfileId } = useParams();
  const profileId = useMemo(() => {
    if (routeProfileId !== undefined) {
      return decodeAndValidatePublicId(routeProfileId);
    }

    const params = new URLSearchParams(location.search);
    const ids = params.getAll("id");

    return ids.length === 1 ? validatePublicId(ids[0]) : null;
  }, [location.search, routeProfileId]);
  const [state, setState] = useState(() => ({
    status: profileId ? "loading" : "invalid",
    handle: null
  }));

  useEffect(() => {
    const controller = new AbortController();
    let timeoutId;

    if (!profileId) {
      setState({ status: "invalid", handle: null });
      return () => controller.abort();
    }

    setState({ status: "loading", handle: null });
    timeoutId = window.setTimeout(() => {
      controller.abort();
      setState({ status: "unavailable", handle: null });
    }, PROFILE_RESOLUTION_TIMEOUT_MS);
    resolveProfileHandle(profileId, { signal: controller.signal })
      .then((handle) => {
        window.clearTimeout(timeoutId);
        setState({ status: "resolved", handle });
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        if (error.name !== "AbortError") {
          setState({ status: "unavailable", handle: null });
        }
      });

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [profileId]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Open a profile on Mondoway | Mondoway";

    return () => {
      document.title = previousTitle;
    };
  }, []);

  if (state.status === "loading") {
    return <LoadingProfile />;
  }

  const isResolved = state.status === "resolved";
  const isInvalid = state.status === "invalid";

  return (
    <AppLinkRedirect
      deepLinkUrl={
        isResolved ? buildDeepLink("profile", state.handle) : null
      }
      invalid={!isResolved}
      openingTitle="Opening this profile in Mondoway..."
      invalidTitle={
        isInvalid
          ? "This profile link is invalid."
          : "This profile is unavailable."
      }
      invalidDescription={
        isInvalid
          ? "The link must contain one valid profile ID."
          : "We could not find the profile handle. Please try again later."
      }
    />
  );
}

export default ProfileRedirect;
